<template>
	<div :class="getModalContainerClass">
		<div class="modal-header">User Settings</div>
		<div
			v-if="!changePassword"
			class="modal-body"
		>
			<div :style="$grid('1')">
				<Panel
					:title="name"
					:action="[
						{
							text: 'Change Password',
							onClick: () => {
								changePassword = true;
							},
						},
					]"
				>
					<div :style="$grid('3-2')">
						<InputTextbox
							label="Email"
							type="email"
							v-model:saturn="email"
							placeholder="Email"
							icon="fas fa-at"
							:disabled="true"
							:class="{ 'grid-2-column': isLoginSameAsEmail }"
						>
						</InputTextbox>

						<InputTextbox
							v-if="!isLoginSameAsEmail"
							label="Login"
							type="text"
							v-model:saturn="login"
							placeholder="Login"
							:disabled="true"
						>
						</InputTextbox>

						<InputTextbox
							label="User Code"
							type="text"
							v-model:saturn="userCode"
							placeholder="User Code"
							:disabled="true"
						>
						</InputTextbox>
						<InputPhone
							label="Cell Phone"
							type="tel"
							v-model:saturn="phone"
							placeholder="Cell Phone"
							maxlength="10"
							minlength="10"
						>
						</InputPhone>
					</div>
				</Panel>

				<Panel
					title="Signature"
					:action="[
						{
							text: 'Re-Sign',
							onClick: () => {
								modal.passedData.toggleESignModal();
							},
						},
					]"
				>
					<div
						:style="$grid('2.18-.9', '5px 20px')"
						class="e-sign-container"
					>
						<div class="signature">
							<div class="caption">Signature</div>
							<div class="reference-line">
								<i class="fas fa-signature"></i>
								<div class="line"></div>
							</div>
							<img
								id="employeeSignature"
								:src="modal.passedData.userInfo.employeeSignature"
							/>
						</div>
						<div class="initials">
							<div class="caption">Initials</div>
							<div class="reference-line">
								<i class="fas fa-signature"></i>
								<div class="line"></div>
							</div>
							<img
								id="employeeInitials"
								:src="modal.passedData.userInfo.employeeInitials"
							/>
						</div>
					</div>
				</Panel>
				<Panel title="Theme">
					<InputRichDropdown
						@change="changeTheme()"
						label="Theme"
						v-model:saturn="theme"
						:list="themes"
						:valueMap="type => type.value"
						:display="type => type.text"
						:overflow="true"
					>
					</InputRichDropdown>
				</Panel>
				<Panel
					title="Partners Credentials"
					:style="$grid('1')"
				>
					<DDIUserSetCredentials
						:ddiCredentials="ddiCredentials"
						:submitBusy="submitBusy"
						:modal="modal"
						:employeeEmail="modal.passedData.userInfo.employeeLogin"
						ref="DDIComponent"
					/>
				</Panel>
				<Panel
					v-if="canNotarize"
					title="Notary Information"
					id="notary-info-panel"
				>
					<div
						v-if="notaryInfoUpdate.length > 0"
						id="notary-info-update"
					>
						<strong>{{ notaryInfoUpdateMessage }}</strong>
					</div>
					<div class="radio-button-inline">
						<span>Licensed as a Notary</span>
						<div class="radio-button-wrapper">
							<InputRadio
								label="Yes"
								:valueMatch="true"
								v-model="userInfo.isNotary"
								:isButton="true"
							/>
							<InputRadio
								label="No"
								:valueMatch="false"
								v-model="userInfo.isNotary"
								:isButton="true"
								@input="clearNotaryInfo"
							/>
						</div>
					</div>
					<button
						v-if="userInfo.isNotary"
						id="add-notary-info-btn"
						class="button button-icon-text button-success"
						:disabled="isNotaryInfoEmpty && userNotaryInformation.length > 0"
						@click="addNotaryStateInfo"
					>
						<i class="fas fa-plus"></i>
						Add Notary State Information
					</button>
					<div
						class="notary-user-info"
						v-if="userInfo.isNotary && notarySettings"
					>
						<NotaryUserInfo
							v-for="notaryInfo in userNotaryInformation"
							:key="notaryInfo.id"
							:fullName="fullName"
							:isNotary="userInfo.isNotary"
							:notaryInfo="notaryInfo"
							:userNotaryInformation="userNotaryInformation"
							:notarySettings="notarySettings"
							@notaryInfoUpdated="handleNotaryInfoUpdated"
							@deleteNotaryInfo="handleDeleteNotaryInfo"
						/>
						<div
							v-if="userNotaryInformation.length === 0"
							id="notary-info-missing"
						>
							No notary information available. Please add notary information for your state
						</div>
					</div>
				</Panel>
			</div>
		</div>
		<div
			v-else
			:class="getModalBodyClass"
		>
			<div
				class="no-margin-grid"
				:style="$grid('1')"
			>
				<InputTextbox
					label="Current Password"
					type="password"
					:invalid="v$.currPass.$invalid"
					v-model:saturn="currPass"
					placeholder="Current Password"
				>
					<template #validation>
						<div v-if="v$.currPass.required.$invalid">Current Password is required</div>
					</template>
				</InputTextbox>
				<InputTextbox
					label="New Password"
					type="password"
					:invalid="isChangePasswordDisabled"
					v-model:saturn="newPass"
					placeholder="New Password"
				>
					<template #validation>
						<div v-if="v$.newPass.required.$invalid">New Password is required</div>
						<div v-else-if="v$.confirmPass.sameAsPassword.$invalid">Confirm Password should be the same as New Password</div>
						<div v-else-if="isChangePasswordDisabled">Must meet password requirements below</div>
					</template>
				</InputTextbox>
				<InputTextbox
					label="Repeat Password"
					type="password"
					:invalid="isChangePasswordDisabled"
					v-model:saturn="confirmPass"
					placeholder="Repeat new Password"
				>
					<template #validation>
						<div v-if="v$.newPass.required.$invalid">New Password is required</div>
						<div v-else-if="v$.confirmPass.sameAsPassword.$invalid">Confirm Password should be the same as New Password</div>
						<div v-else-if="isChangePasswordDisabled">Must meet password requirements below</div>
					</template>
				</InputTextbox>
				<div class="requirements">
					<p
						:class="v$.newPass.meetsMinChars?.$invalid ? 'failure' : 'success'"
						style="font-weight: 600"
					>
						Must contain:
					</p>
					<p>
						<i :class="v$.newPass.meetsMinChars?.$invalid ? 'fas fa-times failure' : 'fas fa-check success'"></i>
						at least 8 characters
					</p>
					<p
						:class="v$.newPass.meetsThree?.$invalid ? 'failure' : 'success'"
						style="font-weight: 600"
					>
						Must meet three of the following:
					</p>
					<p>
						<i :class="v$.newPass.hasLowerCase?.$invalid ? 'fas fa-times failure' : 'fas fa-check success'"></i>
						at least one lowercase letter
					</p>
					<p>
						<i :class="v$.newPass.hasUpperCase?.$invalid ? 'fas fa-times failure' : 'fas fa-check success'"></i>
						at least one uppercase letter
					</p>
					<p>
						<i :class="v$.newPass.hasNumbers?.$invalid ? 'fas fa-times failure' : 'fas fa-check success'"></i>
						at least one number
					</p>
					<p>
						<i :class="v$.newPass.hasNonalphas?.$invalid ? 'fas fa-times failure' : 'fas fa-check success'"></i>
						at least one special character
					</p>
				</div>
			</div>
		</div>
		<div
			v-if="!changePassword"
			class="modal-footer"
		>
			<button
				class="button-unwind"
				@click="cancel()"
			>
				Cancel
			</button>
			<button
				class="button-save"
				@click="close()"
				:disabled="disabledSave"
			>
				<span v-if="!submitBusy">Save</span
				><i
					v-if="submitBusy"
					class="fas fa-cog fa-spin"
				></i>
			</button>
		</div>
		<div
			v-else
			class="modal-footer"
		>
			<button
				class="button-unwind"
				@click="changePassword = false"
			>
				Cancel
			</button>
			<button
				class="button-save"
				@click="saveChangePassword"
				:disabled="isChangePasswordDisabled"
			>
				<span v-if="!submitBusy">Save</span
				><i
					v-if="submitBusy"
					class="fas fa-cog fa-spin"
				></i>
			</button>
		</div>
	</div>
</template>
<script>
	import { EmployeePermissionsType, UserProvideMappingType } from '@core/classes/SharedEnums';
	import { required, sameAs } from '@vuelidate/validators';
	import $modal from '@core/services/modal';
	import { AccessEmployeeClass } from '@core/classes/AccessEmployeeClass';
	import api from '@core/services/api/_api';
	import DDIUserSetCredentials from '@/components/DDIUserSetCredentials.vue';
	import ENUMS from '@core/classes/Enums';
	import InputPhone from '@core/components/InputPhone.vue';
	import InputRadio from '@core/components/InputRadio.vue';
	import InputRichDropdown from '@core/components/InputRichDropdown.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import NotaryUserInfo from '@/components/NotaryUserInfo.vue';
	import Panel from '@core/components/Panel.vue';
	import settings from 'settings';
	import UserProviderMappings from '@core/classes/UserProviderMappings';
	import { useVuelidate } from '@vuelidate/core';
	import util from '@core/services/util';

	export default {
		name: 'modalUserSettings',
		props: ['modal'],
		setup() {
			return { v$: useVuelidate() };
		},
		data() {
			return {
				userInfo: null,
				submitBusy: false,
				stores: null,
				email: null,
				phone: null,
				userCode: null,
				name: null,
				login: null,
				currentSignature: null,
				currentInitials: null,
				currentTheme: null,
				changePassword: false,
				currPass: null,
				newPass: null,
				confirmPass: null,
				theme: 'light-theme',
				themes: [
					{ text: 'Light (Default)', value: 'light-theme' },
					{ text: 'Dark', value: 'dark-theme' },
					{ text: 'Saturn', value: 'saturn-theme' },
					{ text: 'Lucky', value: 'lucky-theme' },
					{ text: 'Solarized-Light', value: 'solarized-light' },
				],
				ddiCredentials: {
					username: '',
					password: '',
				},
				prevIsNotary: false,
				isEditingNotary: false,
				notarySettings: null,
				previousUserNotaryInformation: null,
				userNotaryInformation: null,
				notaryInfoUpdate: [],
				isBusy: false,
			};
		},
		computed: {
			util() {
				return util;
			},
			canNotarize() {
				return this.userInfo.hasAccessToPermission(EmployeePermissionsType.CanNotarize);
			},
			isLoginSameAsEmail() {
				return this.modal.passedData.userInfo.employeeLogin == this.modal.passedData.userInfo.employeeEmail;
			},
			disabledSave() {
				const DDIComponentRef = this.$refs?.DDIComponent;

				if (this.submitBusy) {
					console.log('submitBusy is true');
					return true;
				}

				// If DDI component is loading.
				if (DDIComponentRef && (DDIComponentRef.busyValidatingDDICredentials || DDIComponentRef.encryptingPassword)) {
					console.log('DDI component is busy');
					return true;
				}

				const phoneMatch = this.phone == this.modal.passedData.userInfo.employeePhoneNumber;
				const signatureMatch = this.currentSignature == this.modal.passedData.userInfo.employeeSignature;
				const initialsMatch = this.currentInitials == this.modal.passedData.userInfo.employeeInitials;
				const themeMatch = this.theme == this.modal.passedData.userInfo.employeeTheme;
				const ddiUsernameMatch =
					this.ddiCredentials.username == this.modal.passedData.userInfo.providerMappings?.find(x => x.type === UserProvideMappingType.DDIUsername)?.value;
				const ddiPasswordMatch =
					this.ddiCredentials.password == this.modal.passedData.userInfo.providerMappings?.find(x => x.type === UserProvideMappingType.DDIPassword)?.value;

				const notaryInfoInvalid = (this.userInfo.isNotary && this.isNotaryInfoEmpty) || !this.canNotarize;
				const notaryInfoMatch = JSON.stringify(this.userNotaryInformation) === JSON.stringify(this.previousUserNotaryInformation);
				const isNotaryMatch = this.prevIsNotary == this.userInfo.isNotary;

				const disableSave =
					((isNotaryMatch && notaryInfoMatch) || notaryInfoInvalid) &&
					phoneMatch &&
					signatureMatch &&
					initialsMatch &&
					themeMatch &&
					ddiUsernameMatch &&
					ddiPasswordMatch;

				return disableSave;
			},
			isNotaryInfoEmpty() {
				return (
					this.userNotaryInformation.length === 0 ||
					this.userNotaryInformation.some(item => !item.state || !item.county || !item.notaryCommissionNumber || !item.expirationDate || !item.notaryStampImage)
				);
			},
			fullName() {
				return this.name.split(', ').reverse().toString().replace(',', ' ').toUpperCase();
			},
			isChangePasswordDisabled() {
				return !this.v$.newPass.meetsThree || !this.v$.newPass.meetsMinChars || this.v$.confirmPass.$invalid || this.v$.currPass.$invalid;
			},
			getModalBodyClass() {
				let className = 'modal-body';

				if (this.changePassword) className += ' test';

				return className;
			},
			getModalContainerClass() {
				let className = 'modal-user-settings modal-container modal-medium cobrowsing-censor';

				if (this.changePassword) className += ' modal-change-password';

				return className;
			},
			notaryInfoUpdateMessage() {
				return this.notaryInfoUpdate.map(info => info.message).join(', ');
			},
			ENUMS() {
				return ENUMS;
			},
		},
		created() {
			this.loadData();
		},
		mounted() {
			if (this.notaryInfoUpdate.length > 0) {
				this.$nextTick(() => {
					const notaryInfoPanel = document.getElementById('notary-info-panel');
					if (notaryInfoPanel) {
						notaryInfoPanel.scrollIntoView({ behavior: 'smooth' });
					}
				});
			}

			// Since the signature data is being updated outside of the modal (in UserInfo component), we need to watch for changes in the passedData and update the local userInfo accordingly.
			this.$watch(
				() => this.modal.passedData.userInfo.employeeSignature,
				(newValue, oldValue) => {
					this.userInfo.employeeSignature = this.modal.passedData.userInfo.employeeSignature;
					this.userInfo.employeeInitials = this.modal.passedData.userInfo.employeeInitials;
					this.userInfo.signatureData = this.modal.passedData.userInfo.signatureData;
					this.userInfo.initialsData = this.modal.passedData.userInfo.initialsData;
				},
			);
		},
		methods: {
			async close() {
				this.submitBusy = true;
				this.userInfo.employeePhoneNumber = this.phone;
				this.userInfo.employeeTheme = this.theme;

				this.setDDICredentials();

				api.users
					.setUser(this.userInfo, [])
					.then(response => {
						util.toastr('success', 'User Settings Update', 'Settings Changed Successfully');
						localStorage.setItem('Saturn-Theme', this.theme);
						$modal.close(this.userInfo);
					})
					.catch(err => {
						util.toastr('error', 'User Settings Update', err);
					})
					.finally(() => {
						this.submitBusy = false;
					});
			},
			cancel() {
				this.userInfo.employeeSignature = this.currentSignature;
				this.userInfo.employeeInitials = this.currentInitials;
				this.theme = this.currentTheme;
				this.changeTheme();
				$modal.cancel();
			},
			async loadData() {
				this.userInfo = new AccessEmployeeClass(this.modal.passedData.userInfo);

				this.name = this.userInfo.employeeName;
				this.login = this.userInfo.employeeLogin;
				this.email = this.userInfo.employeeEmail;
				this.userCode = this.userInfo.employeeCode;
				this.stores = settings.userStores;
				this.phone = this.userInfo.employeePhoneNumber;
				this.currentSignature = this.userInfo.employeeSignature;
				this.currentInitials = this.userInfo.employeeInitials;
				this.theme = this.userInfo.employeeTheme ?? 'light-theme';
				this.currentTheme = this.theme;

				this.ddiCredentials.username = this.userInfo.providerMappings?.find(x => x.type === UserProvideMappingType.DDIUsername)?.value;
				this.ddiCredentials.password = this.userInfo.providerMappings?.find(x => x.type === UserProvideMappingType.DDIPassword)?.value;

				this.prevIsNotary = this.userInfo.isNotary;
				this.userNotaryInformation = this.userInfo?.userNotaryInformation ? [...this.userInfo.userNotaryInformation] : [];
				this.previousUserNotaryInformation = [...this.userNotaryInformation]; // used to compare if notary info is changed to disable the save
				this.notaryInfoUpdate = [...this.modal.passedData.notaryInfoUpdate];
				await this.getNotarySettings();
			},
			async saveChangePassword() {
				this.submitBusy = true;
				const response = await api.users.updateUserPassword(this.email, this.currPass, this.newPass);
				if (response.data == 'Incorrect Credentials') {
					util.toastr('error', 'Error!', 'Current Password is incorrect');
				} else {
					this.changePassword = false;
					util.toastr('success', 'Success!', 'Successfully changed Password');
				}
				this.submitBusy = false;
			},
			changeTheme() {
				document.body.setAttribute('class', this.theme);

				const colorScheme = window.getComputedStyle(document.body)?.getPropertyValue('color-scheme');
				document.body.classList.add(colorScheme === 'dark' ? 'dark-palette' : 'light-palette');
			},
			handleNotaryInfoUpdated(updatedUserNotaryInformation) {
				const existingIndex = this.userNotaryInformation.findIndex(item => item.id === updatedUserNotaryInformation.id);
				if (existingIndex !== -1) {
					this.userNotaryInformation[existingIndex] = {
						...updatedUserNotaryInformation,
					};
				} else {
					this.userNotaryInformation.push({ ...updatedUserNotaryInformation });
				}
				this.userInfo.userNotaryInformation = this.userNotaryInformation;
			},
			handleDeleteNotaryInfo(id) {
				const index = this.userNotaryInformation.findIndex(item => item.id === id);
				if (index !== -1) {
					this.userNotaryInformation.splice(index, 1);
				}
				this.userInfo.userNotaryInformation = this.userNotaryInformation;
			},
			addNotaryStateInfo() {
				this.userNotaryInformation = [
					{
						id: util.getGuid(),
						state: '',
						county: '',
						notaryCommissionNumber: '',
						expirationDate: '',
						notaryStampImage: '',
					},
					...this.userNotaryInformation,
				];
			},
			clearNotaryInfo() {
				this.userNotaryInformation = [];
				this.userInfo.userNotaryInformation = this.userNotaryInformation;
			},
			setDDICredentials() {
				const userInfo = this.modal.passedData.userInfo;

				const hasUsername = !util.isNullOrEmpty(this.ddiCredentials.username);
				const hasPassword = !util.isNullOrEmpty(this.ddiCredentials.password);

				// Filter out username mapping if username is missing
				if (!hasUsername) {
					userInfo.providerMappings = userInfo.providerMappings.filter(x => x.type !== UserProvideMappingType.DDIUsername);
				}

				// Update or add username mapping if username is present
				if (hasUsername) {
					const usernameIndex = userInfo.providerMappings.findIndex(x => x.type === UserProvideMappingType.DDIUsername);
					if (usernameIndex >= 0) {
						// Update existing username mapping
						userInfo.providerMappings[usernameIndex].value = this.ddiCredentials.username;
						userInfo.providerMappings[usernameIndex].dateUpdated = new Date();
					} else {
						// Add new username mapping if not found
						const usernameMapping = new UserProviderMappings({
							value: this.ddiCredentials.username,
							type: UserProvideMappingType.DDIUsername,
						});
						userInfo.providerMappings.push(usernameMapping);
					}
				}

				// Filter out password mapping if password is missing
				if (!hasPassword) {
					userInfo.providerMappings = userInfo.providerMappings.filter(x => x.type !== UserProvideMappingType.DDIPassword);
				}

				// Update or add password mapping if password is present
				if (hasPassword) {
					const passwordIndex = userInfo.providerMappings.findIndex(x => x.type === UserProvideMappingType.DDIPassword);
					if (passwordIndex >= 0) {
						// Update existing password mapping
						userInfo.providerMappings[passwordIndex].value = this.ddiCredentials.password;
						userInfo.providerMappings[passwordIndex].dateUpdated = new Date();
					} else {
						// Add new password mapping if not found
						const passwordMapping = new UserProviderMappings({
							value: this.ddiCredentials.password,
							type: UserProvideMappingType.DDIPassword,
						});
						userInfo.providerMappings.push(passwordMapping);
					}
				}
			},
			async getNotarySettings() {
				try {
					this.isBusy = true;
					const response = await api.settings.getNotarySettings();

					if (response.data) {
						this.notarySettings = response.data;
					}
				} catch (error) {
					console.error(error);
				} finally {
					this.isBusy = false;
				}
			},
		},
		components: {
			InputTextbox,
			InputRadio,
			InputRichDropdown,
			NotaryUserInfo,
			// InputDate,
			InputPhone,
			DDIUserSetCredentials,
			// NotaryStampPreview,
			Panel,
		},
		validations() {
			return {
				currPass: { required },
				newPass: {
					required,
					hasUpperCase: value => /[A-Z]/.test(value),
					hasLowerCase: value => /[a-z]/.test(value ?? ''),
					hasNumbers: value => /\d/.test(value),
					hasNonalphas: value => /\W/.test(value),
					meetsThree: value => /[A-Z]/.test(value) + /[a-z]/.test(value) + /\d/.test(value) + /\W/.test(value) >= 3,
					meetsMinChars: value => (value?.length ?? 0) >= 8,
				},
				confirmPass: {
					required,
					sameAsPassword: value => value === this.newPass,
				},
			};
		},
	};
</script>
<style>
	.modal-user-settings.modal-container.modal-medium.modal-change-password .modal-body {
		height: 330px;
		min-height: 330px;
	}
	.modal-user-settings.modal-container.modal-medium .modal-body {
		min-height: 545px;
		max-height: 600px;
		transition: var(--transition);
	}
	.modal-user-settings.modal-container.modal-medium.modal-change-password {
		width: 325px;
	}
	.modal-user-settings.modal-container.modal-medium {
		transition: var(--transition);
	}
	.requirements .success {
		color: var(--success-color);
	}

	.requirements .failure {
		color: var(--error-color);
	}
	.requirements p {
		margin-block-start: 2px;
		margin-block-end: 2px;
	}
	.requirements i {
		width: 30px;
		text-align: center;
	}
	.modal-user-settings .panel-header-button-array button {
		white-space: nowrap;
	}

	#add-notary-info-btn {
		max-width: fit-content;
		margin-top: 10px;
		margin-bottom: 10px;
	}

	#notary-info-missing {
		padding: 10px;
		border-radius: 5px;
		background-color: #ddd;
	}

	#notary-info-update {
		padding: 1.2rem;
		margin: 1.2rem;
		border-radius: 5px;
		background-color: rgba(247, 2, 2, 0.23);
		animation: notaryInfoUpdatePulse 2.5s infinite;
		border: 2px solid rgba(247, 2, 2, 0.23);
	}

	@keyframes notaryInfoUpdatePulse {
		0% {
			box-shadow: 0 0 0 0 rgb(238, 56, 56, 0.35);
		}

		70% {
			box-shadow: 0 0 0 10px rgb(238, 56, 56, 0.15);
		}

		100% {
			box-shadow: 0 0 0 0 rgb(238, 56, 56, 0);
		}
	}
</style>
